import { CircleAlertIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';

import type { UseAudiencePageReturn } from '../hooks/useAudiencePage';
import { ActiveFilterChip, ErrorChip, Subscriber } from './Tables/AudienceTable/columns';
import { AudienceTable } from './Tables/AudienceTable/Table';

// Shared building blocks for the Review Audience screen (outreach + payment recovery).

export const AudienceSectionHeader = ({
  variant,
  title,
  count,
}: {
  variant: 'required' | 'warning';
  title: string;
  count: number;
}) => (
  <div className="flex shrink-0 items-center gap-2 self-stretch rounded-md border border-stroke bg-background px-4 py-3">
    {variant === 'required' ? (
      <CircleAlertIcon className="size-4 shrink-0 text-destructive" />
    ) : (
      <TriangleAlertIcon className="size-4 shrink-0 text-warning" />
    )}
    <span className="text-sm leading-5 font-semibold text-foreground">{title}</span>
    <span className="text-sm leading-5 font-normal text-foreground-muted">({count})</span>
  </div>
);

export const AudienceIssueRow = ({
  title,
  detail,
  actionLabel,
  onAction,
}: {
  title: string;
  detail: string;
  actionLabel: string;
  onAction: () => void;
}) => (
  <div className="flex shrink-0 items-center gap-2 self-stretch py-2 pr-4 pl-9.5">
    <div className="flex flex-1 flex-col">
      <span className="text-sm leading-5 font-medium text-foreground">{title}</span>
      <span className="self-stretch text-xs leading-5 font-normal text-foreground-muted">
        {detail}
      </span>
    </div>
    <Button variant="ghost" size="sm" className="leading-6 text-primary" onClick={onAction}>
      {actionLabel}
    </Button>
  </div>
);

export const AudienceErrorChipsBar = ({
  chips,
  onRemove,
}: {
  chips: ErrorChip[];
  onRemove: (key: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex h-10 shrink-0 items-start gap-2 self-stretch rounded-lg border border-stroke bg-background py-1 pr-1 pl-1.5">
      <div className="flex shrink-0 grow basis-0 flex-wrap content-center items-center gap-1.5 self-stretch">
        {chips.map((chip) => (
          <ActiveFilterChip
            key={chip.key}
            column={chip.column}
            operator={t('analytics.reports_detail_filter_op_is')}
            value={chip.value}
            onRemove={() => onRemove(chip.key)}
          />
        ))}
      </div>
    </div>
  );
};

// min-h floor: warnings/chips push the table down but never crush it; the page scrolls instead.
export const ReviewAudienceTable = ({
  page,
  data,
  rowCount,
}: {
  page: UseAudiencePageReturn;
  data: Subscriber[];
  rowCount: number;
}) => {
  const hiddenColumnsIndicator = page.hasHiddenColumns ? (
    <HiddenColumnsIndicator
      onCustomize={() => page.setIsCustomizeColumnsOpen((prev) => !prev)}
      buttonRef={page.customizeButtonRef}
    />
  ) : undefined;

  return (
    <div className="flex min-h-80 flex-1 flex-col">
      <AudienceTable
        columns={page.columns}
        data={data}
        rowCount={rowCount}
        isLoading={page.isLoading}
        hasSearch={page.hasSearch}
        isServerPaginated={!page.isShowingReduxData}
        currentPageIndex={page.currentPageIndex}
        setCurrentPageIndex={page.setCurrentPageIndex}
        fetchNextPage={page.goToNextPage}
        hasNextPage={page.hasNextPage}
        isFetchingNextPage={page.isFetchingNextPage}
        columnVisibility={page.columnVisibility}
        onColumnVisibilityChange={page.setColumnVisibility}
        paginationCenterContent={hiddenColumnsIndicator}
      />
    </div>
  );
};
